import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { useBlockchainContext } from '@sorare/core/src/contexts/blockchain';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { txLink } from '@sorare/core/src/lib/etherscan';
import { glossary } from '@sorare/core/src/lib/glossary';

import { FlexCard } from '#src/components/token/FlexCard/index.tsx';

import type { EthereumCard_anyCard } from './__generated__/index.graphql.ts';

const CARD_DIALOG_WIDTH = 144;

const Container = styled.div`
  max-width: ${CARD_DIALOG_WIDTH}px;
  gap: var(--unit);
  display: flex;
  flex-direction: column;
`;

const Card = styled.div`
  width: ${CARD_DIALOG_WIDTH}px;
  display: block;
`;

const DepositButton = styled(Button)`
  width: 100%;
`;

type Props = {
  card: EthereumCard_anyCard;
};

export const EthereumCard = ({ card }: Props) => {
  const { showNotification } = useSnackNotificationContext();
  const [depositing, setDepositing] = useState(false);
  const [deposited, setDeposited] = useState(false);
  const blockchains = useBlockchainContext();

  const deposit = async () => {
    setDepositing(true);
    const result = await blockchains.depositNft(
      card.contractAddress!,
      card.ethereumId!
    );
    setDepositing(false);

    if (result?.err) {
      showNotification('errors', { errors: result.err });
    } else {
      const txHash = result?.txHash;
      const link = txHash ? txLink(txHash) : undefined;

      if (result?.type === 'deposit')
        showNotification('ethereumCard', {}, { link });
      if (result?.type === 'approval')
        showNotification('bridgeApproval', {}, { link });

      setDeposited(true);
    }
  };

  return (
    <Container>
      <Card>
        <FlexCard card={card} withLink />
      </Card>
      {!deposited && (
        <DepositButton
          onClick={() => {
            deposit();
          }}
          color="quaternary"
          disabled={deposited || !!card.tokenOwner?.optimistic}
          loading={depositing}
          size="medium"
        >
          <FormattedMessage {...glossary.depositAction} />
        </DepositButton>
      )}
    </Container>
  );
};

EthereumCard.fragments = {
  anyCard: gql`
    fragment EthereumCard_anyCard on AnyCardInterface {
      slug
      ethereumId
      contractAddress
      tokenOwner {
        id
        optimistic
      }
      ...FlexCard_anyCard
    }
    ${FlexCard.fragments.anyCard}
  ` as TypedDocumentNode<EthereumCard_anyCard>,
};
